import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QrCode, Search, ChevronRight, Home } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { API, SMART_QR_CATEGORIES, inputCls } from '../../components/smartqr/smartqrApi';
import { ArrivalCard } from '../../components/smartqr/NewArrivals';

const PAGE_SIZE = 12;
const chipCls = (on) => `px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${on ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`;

export const SmartQRDirectoryPage = () => {
  const [params, setParams] = useSearchParams();
  const city = params.get('city') || '';
  const category = params.get('category') || '';
  const [q, setQ] = useState(params.get('q') || '');
  const [data, setData] = useState({ items: [], total: 0, has_more: false, cities: [] });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
  };

  const fetchPage = useCallback(async (p, append) => {
    setLoading(true);
    const qs = new URLSearchParams({ page: String(p), limit: String(PAGE_SIZE) });
    if (city) qs.set('city', city);
    if (category) qs.set('category', category);
    const term = params.get('q') || '';
    if (term) qs.set('q', term);
    try {
      const r = (await axios.get(`${API}/api/smart-qr/businesses?${qs}`)).data;
      setData((prev) => ({ ...r, items: append ? [...prev.items, ...r.items] : r.items }));
      setPage(p);
    } catch { setData((prev) => ({ ...prev, items: append ? prev.items : [], has_more: false })); }
    setLoading(false);
  }, [city, category, params]);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);

  const submitSearch = (e) => { e.preventDefault(); setFilter('q', q.trim()); };
  const showing = data.items.length;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="smartqr-directory-page">
      <SEO title={`All Smart Business QR Shops${city ? ` in ${city}` : ''} | JustOffs`} description="Browse every business with a JustOffs Smart Business QR page — products, offers, WhatsApp, call and directions in one scan." />
      <Header />
      <main className="flex-1">
        <section className="bg-slate-800 text-white py-8">
          <div className="max-w-6xl mx-auto px-4">
            <nav className="text-xs text-slate-300 flex items-center gap-1.5 mb-3" data-testid="directory-breadcrumb"><Link to="/" className="hover:text-white inline-flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Home</Link> <ChevronRight className="w-3 h-3" /> <span className="text-white">New Arrivals</span></nav>
            <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2"><QrCode className="w-7 h-7 text-orange-400" /> ALL SMART BUSINESS QR SHOPS</h1>
            <p className="text-sm text-slate-300 mt-1">Every shop with a JustOffs Smart Business QR page{city ? ` in ${city}` : ''}. Scan, browse products and connect on WhatsApp.</p>
            <form onSubmit={submitSearch} className="mt-4 flex gap-2 max-w-lg" data-testid="directory-search-form">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shop name..." className={`${inputCls} text-slate-800`} data-testid="directory-search-input" />
              <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded-lg font-semibold text-sm inline-flex items-center gap-1 transition-colors" data-testid="directory-search-btn"><Search className="w-4 h-4" /> <span className="hidden sm:inline">Search</span></button>
            </form>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-6">
          {data.cities.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-testid="directory-city-filter">
              <button onClick={() => setFilter('city', '')} className={chipCls(!city)} data-testid="directory-city-all">All Cities</button>
              {data.cities.map((c) => <button key={c} onClick={() => setFilter('city', c)} className={chipCls(city.toLowerCase() === c.toLowerCase())} data-testid={`directory-city-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</button>)}
            </div>
          )}
          <div className="flex gap-2 overflow-x-auto pb-2 mt-1 -mx-4 px-4 scrollbar-hide" data-testid="directory-category-filter">
            <button onClick={() => setFilter('category', '')} className={chipCls(!category)} data-testid="directory-cat-all">All Categories</button>
            {SMART_QR_CATEGORIES.map((c) => <button key={c.id} onClick={() => setFilter('category', c.id)} className={chipCls(category === c.id)} data-testid={`directory-cat-${c.id}`}>{c.name}</button>)}
          </div>

          <p className="text-xs text-slate-500 mt-3 mb-3" data-testid="directory-count">{loading && showing === 0 ? 'Loading shops...' : `Showing ${showing} of ${data.total} shop${data.total === 1 ? '' : 's'}`}</p>

          {!loading && showing === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="directory-empty">
              <p className="font-semibold text-slate-700">No shops match these filters yet.</p>
              <p className="text-xs text-slate-500 mt-1 mb-4">Be the first — get your free Smart Business QR in 2 minutes.</p>
              <Link to="/smart-business-qr" className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full text-sm font-bold" data-testid="directory-empty-cta"><QrCode className="w-4 h-4" /> GET SMART QR</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="directory-grid">{data.items.map((b) => <ArrivalCard key={b.public_slug} b={b} />)}</div>
          )}

          {data.has_more && (
            <div className="text-center mt-6">
              <button onClick={() => fetchPage(page + 1, true)} disabled={loading} className="bg-slate-800 hover:bg-slate-900 text-white px-8 py-3 rounded-full font-bold text-sm disabled:opacity-60 transition-colors" data-testid="directory-load-more">{loading ? 'Loading...' : 'LOAD MORE SHOPS'}</button>
            </div>
          )}

          <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div><p className="font-bold text-slate-800 text-sm">Own a shop? Get listed here for free.</p><p className="text-xs text-slate-500">One permanent QR, your own business page, products & WhatsApp enquiries.</p></div>
            <Link to="/smart-business-qr" className="inline-flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap" data-testid="directory-get-qr-cta"><QrCode className="w-4 h-4" /> GET SMART QR</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
